import PrintIcon from '@mui/icons-material/Print';
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { Form } from '@pdfme/ui';
import printJS from 'print-js';
import { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { ICertificate } from '../../entities/certificates';
import { ITemplate } from '../../entities/organization';
import { useTemplateFonts } from '../../lib/contexts/TemplateFonts';
import { formatDate, getDiplomaSerialNumber, removeAccents, undefinedSigleLabel } from '../../lib/helper';
import { AppDispatch, IState } from '../../lib/store';
import { useTemplates } from '../../lib/store/organization/useTemplates';
import { updateCertificate } from '../../lib/store/projects/thunks';
import BeforePrintPreview from '../BeforePrintPreview';
import ConfirmationDialog from '../ConfirmationDialog';
import { enhanceMetadata, enhanceTemplate, generatePDF, getTemplate } from './helper';
import { getTemplateLabels } from '../../lib/Template';

interface IProps {
    certificate: ICertificate;
    templateVersion: string;
}

const ConfirmBody: React.FC<{serialNumber: string}> = ({serialNumber}) => {
    return (
        <Box>
            <Typography>Diplôme imprimé avec succès ? veuillez confirmer si tel est le cas.</Typography>
            <span><strong>Numéro de serie: <span style={{color: serialNumber.includes(undefinedSigleLabel) ? 'red' : ''}}>{serialNumber}</span></strong> </span>
        </Box>
    )
}

const Component: React.FC<IProps> = ({certificate, templateVersion}) => {
    const dispatch = useDispatch<AppDispatch>();
    const { templates, ready } = useTemplates();
    const [diplomaTemplate, setDiplomaTemplate] = useState<ITemplate>();
    const [blankTemplate, setBlankTemplate] = useState<ITemplate>();
    const [templateNotFound, setTemplateNotFound] = useState<boolean>(false);
    const [openAckDialog, setOpenAckDialog] = useState<boolean>(false);
    const [openPreviewModal, setOpenPreviewModal] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [metadata, setMetadata] = useState<Record<string, string>>({});
    const universityName = useSelector((state: IState) => state?.organization?.organization?.name);
    const [form, setForm] = useState<Form>();
    const templateFonts = useTemplateFonts();
    const [generationLoading, setGenerationLoading] = useState<boolean>(false);

    useEffect(() => {
        if (!ready) {
            return;
        }
        const dTpl = getTemplate(removeAccents(certificate!.metadata!['grade']), removeAccents(certificate!.metadata!['program_etude']), removeAccents(certificate!.metadata!['institution']))(templates, templateVersion);
        const blankTpl = getTemplate('Blank')(templates, templateVersion);
        if (!dTpl || !dTpl.print) {
            setTemplateNotFound(true);
            return;
        }

        const bTpl = {
            ...dTpl?.print,
            template: {
                ...dTpl?.print.template,
                basePdf: blankTpl?.preview.template.basePdf!,
            },
        }

        const metad = enhanceMetadata({...certificate!.metadata}, universityName, templateVersion);
        setMetadata(enhanceMetadata({...certificate!.metadata}, universityName, templateVersion));
        setDiplomaTemplate(enhanceTemplate(JSON.parse(JSON.stringify(dTpl.print)), metad['ecole_institut'] !== ' ', universityName,  metad['specialite']));
        setBlankTemplate(enhanceTemplate(JSON.parse(JSON.stringify(bTpl)), metad['ecole_institut'] !== ' ', universityName,  metad['specialite']));
    }, [templateVersion, certificate, ready, templates]);
    
    const onPrintDialogClose = () => setOpenAckDialog(true);

    const print = async () => {
        setGenerationLoading(true);
        const inputs = [metadata];
        try {
            const res = await generatePDF(blankTemplate!.id!, {[certificate.ine]: inputs});
            const file = new File([new Uint8Array(res[certificate.ine]?.data)], `${certificate.ine}.pdf`, { type: "application/octet-stream" });
            const url = URL.createObjectURL(file);
            setGenerationLoading(false);
            setOpenPreviewModal(false);
            printJS({
                printable: url,
                type: 'pdf',
                onPrintDialogClose,
            });
        } catch(e) {
            setGenerationLoading(false);
            setOpenPreviewModal(false);
            toast.error('Erreur d\'impression, veuillez contacter le support');
            console.log('pdf generation error');
        }
    }

    const onPrinted = () => {
        const cert: ICertificate = Object.assign({}, certificate);
        cert.printed = true;
        cert.printDate = Date.now();
        cert.metadata = Object.assign({}, certificate.metadata);
        cert.metadata!['serial_number'] = metadata['serial_number'];
        delete cert.metadata['_id'];
        setLoading(true);
        dispatch(updateCertificate(cert)).unwrap()
            .then(() => toast.success('Impression terminée'))
            .catch(() => toast.error('Une erreur est survenue, veuillez contacter le support si le problème persiste...'))
            .finally(() => {
                setLoading(false);
                setOpenAckDialog(false);
            });
    }
    
    const openModal = () => {
        const labels = Object.keys(getTemplateLabels(metadata['grade'], metadata['program_etude']));
        const unsets: string[] = [];
        diplomaTemplate?.template.columns?.filter((c) => !labels.includes(c)).map((c: string) => {
            if (!(metadata && metadata[c])) {
                unsets.push(c);
            }
        });

        if (unsets.length > 0) {
            toast.error(
                <>
                    <span>Ces champs suivant du diplome sont manquants ou mal formattés:</span>
                    <ul>
                        {unsets.map((c) => {
                            if (c == 'recteur_valeur_gauche') return <li key={c} >Signataire 1</li>;
                            if (c == 'recteur_valeur_droite') return <li key={c} >Signataire 2</li>;
                            return <li key={c} >{c}</li>
                        })}
                    </ul>
                </>
            );
            return;
        }

        setOpenPreviewModal(true);
    }

    if (!ready) {
        return <CircularProgress size={22} sx={{color: '#005e7a'}} />;
    }

    return (
        <>
            {templateNotFound && <span style={{color: 'red', fontSize: '15px'}}> template version: {templateVersion} introuvable </span>}
            {!templateNotFound && ( // && !certificate.printed 
                <Button variant="outlined" color="success" onClick={openModal} >
                    <PrintIcon  />
                </Button>
            )}

            {/* {certificate.printed && (
                <span style={{paddingRight: '1rem', marginRight: '1rem', color: 'green'}}>
                    Diplôme imprimé le {formatDate(certificate.printDate! * 1000)}
                </span>
            )} */}

            {true && (
                <>
                    <ConfirmationDialog
                        open={openAckDialog}
                        close={() => setOpenAckDialog(false)}
                        actionLabel='Retour impression'
                        contentText={<ConfirmBody serialNumber={metadata['serial_number']} />}
                        succesLabel='Oui'
                        errorLabel='Non'
                        loading={loading}
                        action={onPrinted}
                        />
                
                    <BeforePrintPreview 
                        template={diplomaTemplate?.template!} 
                        inputs={[{...metadata, ...getTemplateLabels(metadata['grade'], metadata['program_etude'])}]} 
                        open={openPreviewModal} 
                        close={() => setOpenPreviewModal(false)}
                        action={print}
                        setForm={setForm}
                        loading={generationLoading}
                        />
                </>
            )}
        </>
    )
}

export default Component;